import React, { useState, useRef } from 'react';

// Stock photos organized by gender selection
const STOCK_PHOTOS = {
  male: {
    topLeft: '/stock-images/female-1.jpg',
    bottomLeft: '/stock-images/female-2.jpg', 
    bottomRight: '/stock-images/male-1.jpg'
  },
  female: {
    topLeft: '/stock-images/male-1.jpg',
    bottomLeft: '/stock-images/male-2.jpg',
    bottomRight: '/stock-images/female-1.jpg'
  },
  'non-binary': {
    topLeft: '/stock-images/male-1.jpg',
    bottomLeft: '/stock-images/female-1.jpg',
    bottomRight: '/stock-images/male-2.jpg'
  }
};

const DoubbleMatchGenerator: React.FC = () => {
  const [selectedGender, setSelectedGender] = useState<string | null>(null);
  const [userImage, setUserImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setUserImage(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-primary p-4">
      {!selectedGender ? (
        // Gender selection state
        <div className="text-center">
          <h1 className="text-2xl font-bold text-black mb-8">DoubbleMatch Screenshot</h1>
          <p className="text-lg text-black mb-8" style={{ fontFamily: 'Cera Pro, sans-serif' }}>
            Select your gender to get started:
          </p>
          <div className="space-y-4">
            <button
              onClick={() => setSelectedGender('male')}
              className="bg-white text-black px-8 py-4 rounded-full text-lg font-semibold shadow-lg hover:shadow-xl transition-shadow block mx-auto w-48"
            >
              Male
            </button>
            <button
              onClick={() => setSelectedGender('female')}
              className="bg-white text-black px-8 py-4 rounded-full text-lg font-semibold shadow-lg hover:shadow-xl transition-shadow block mx-auto w-48"
            >
              Female
            </button>
            <button
              onClick={() => setSelectedGender('non-binary')}
              className="bg-white text-black px-8 py-4 rounded-full text-lg font-semibold shadow-lg hover:shadow-xl transition-shadow block mx-auto w-48"
            >
              Non-binary
            </button>
          </div>
        </div>
      ) : !userImage ? (
        // Upload state
        <div className="text-center">
          <h1 className="text-2xl font-bold text-black mb-8">DoubbleMatch Screenshot</h1>
          <button
            onClick={handleUploadClick}
            className="bg-white text-black px-8 py-4 rounded-full text-lg font-semibold shadow-lg hover:shadow-xl transition-shadow"
          >
            Upload Your Photo
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            className="hidden"
          />
          <div className="mt-4">
            <button
              onClick={() => setSelectedGender(null)}
              className="text-black underline"
              style={{ fontFamily: 'Cera Pro, sans-serif' }}
            >
              ← Back to gender selection
            </button>
          </div>
        </div>
      ) : (
        // Generated doubblematch view
        <div className="w-full max-w-sm mx-auto">


          {/* Close button */}
          <div className="flex justify-center mb-6">
            <div className="relative w-56">
              <button 
                onClick={() => {
                  setUserImage(null);
                  setSelectedGender(null);
                }}
                className="absolute top-0 right-0 text-black text-lg font-light"
              >
                ✕
              </button>
            </div>
          </div>

          {/* doubblematch title */}
          <h1 className="text-2xl text-black text-center mb-8" style={{ fontFamily: 'RZA, sans-serif' }}>
            DoubbleMatch!
          </h1>

          {/* Four clover shapes - precise positioning */}
          <div className="relative w-56 h-56 mx-auto mb-8">
            {/* Top Left Clover - Man on rooftop (sharp corner: bottom-right) */}
            <div 
              className="absolute w-28 h-28 bg-gray-300 overflow-hidden"
              style={{
                top: '0px', 
                left: '0px',
                borderRadius: '70px 70px 0px 70px'
              }}
            >
              <img 
                src={STOCK_PHOTOS[selectedGender as keyof typeof STOCK_PHOTOS]?.topLeft} 
                alt="Person 1"
                className="w-full h-full object-cover"
              />
            </div>
            
            {/* Top Right Clover - User's image (sharp corner: bottom-left) */}
            <div 
              className="absolute w-28 h-28 bg-gray-300 overflow-hidden"
              style={{
                top: '0px',
                left: '116px',
                borderRadius: '70px 70px 70px 0px'
              }}
            >
              <img 
                src={userImage} 
                alt="User uploaded"
                className="w-full h-full object-cover"
              />
            </div>
            
            {/* Bottom Left Clover - Pilot man (sharp corner: top-right) */}
            <div 
              className="absolute w-28 h-28 bg-gray-300 overflow-hidden"
              style={{
                top: '116px',
                left: '0px',
                borderRadius: '70px 0px 70px 70px'
              }}
            >
              <img 
                src={STOCK_PHOTOS[selectedGender as keyof typeof STOCK_PHOTOS]?.bottomLeft} 
                alt="Person 2"
                className="w-full h-full object-cover"
              />
            </div>
            
            {/* Bottom Right Clover - Blonde woman (sharp corner: top-left) */}
            <div 
              className="absolute w-28 h-28 bg-gray-300 overflow-hidden"
              style={{
                top: '116px',
                left: '116px',
                borderRadius: '0px 70px 70px 70px'
              }}
            >
              <img 
                src={STOCK_PHOTOS[selectedGender as keyof typeof STOCK_PHOTOS]?.bottomRight} 
                alt="Person 3"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Match text */}
          <div className="text-center text-black mb-8" style={{ fontFamily: 'Cera Pro, sans-serif' }}>
            <p className="text-xs">
              <span className="font-bold">You</span> matched with <span className="font-bold">Patrick</span>
            </p>
            <p className="text-xs">
              <span className="font-bold">Maya</span> matched with <span className="font-bold">his</span> friend
            </p>
          </div>

          {/* Go to group chat button */}
          <div className="text-center">
            <button className="bg-white bg-opacity-90 text-black px-3 py-3 rounded-full text-xs shadow-lg flex items-center justify-center mx-auto" style={{ fontFamily: 'Cera Pro, sans-serif' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12.0002 2C10.2356 1.99921 8.50232 2.46555 6.97639 3.35164C5.45045 4.23772 4.18627 5.51197 3.31233 7.04489C2.43838 8.57781 1.9858 10.3148 2.0006 12.0792C2.01539 13.8437 2.49702 15.5729 3.39655 17.0909L2.05564 20.78C2.00567 20.9173 1.98953 21.0646 2.00858 21.2094C2.02762 21.3543 2.08129 21.4924 2.16505 21.6121C2.2488 21.7318 2.36017 21.8295 2.48972 21.8971C2.61927 21.9646 2.76318 21.9999 2.90927 22C3.01527 21.9997 3.12043 21.9813 3.22018 21.9455L6.90927 20.6036C8.23325 21.388 9.72038 21.8561 11.2549 21.9717C12.7894 22.0873 14.3299 21.8472 15.7564 21.2701C17.183 20.693 18.4571 19.7944 19.4795 18.6443C20.502 17.4943 21.2453 16.1238 21.6514 14.6395C22.0576 13.1552 22.1158 11.5972 21.8213 10.0867C21.5269 8.57631 20.8879 7.15421 19.954 5.93114C19.02 4.70807 17.8165 3.71704 16.4369 3.03519C15.0573 2.35335 13.539 1.99909 12.0002 2Z" fill="black"/>
            </svg>
              Go to group chat
              <span className="ml-3">❯</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DoubbleMatchGenerator;
